import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import {
    AssignCertificateResponse,
    BulkCertificateAssignmentPayload,
    BulkCertificateAssignmentResponse,
    CertificateFilterResponse,
    CertificateTemplatesResponse,
    CertificateTypeUpdatePayload,
    CertificateTypeUpdateResponse,
    PhysicalCertificateTogglePayload,
    PhysicalCertificateToggleResponse,
    SaveTemplatePayload,
} from '@lib/interfaces/certificate.interface';
import { Observable, catchError, tap, throwError } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class CertificateService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = environment.API_URL;

    // Method to get certificate templates by course ID
    getCertificateTemplates(courseId?: number): Observable<CertificateTemplatesResponse> {
        let params = new HttpParams();

        if (courseId) {
            params = params.set('courseId', courseId.toString());
        }

        const url = `${this.baseUrl}/certificate/templates`;

        return this.http.get<CertificateTemplatesResponse>(url, { params });
    }

    // Method to assign certificate templates to a course
    // Only includes selected templates in payload to avoid overwriting existing data in PATCH request
    saveSelectedTemplates(courseId: number, payload: SaveTemplatePayload): Observable<AssignCertificateResponse> {
        const { completionTemplateId, participationTemplateId } = payload;

        // Create request body with the correct field names as per API documentation
        const requestBody: any = {};

        // Add template IDs if they exist
        if (completionTemplateId) {
            requestBody.completionTemplateId = completionTemplateId;
        }
        if (participationTemplateId) {
            requestBody.participationTemplateId = participationTemplateId;
        }

        return this.http
            .patch<AssignCertificateResponse>(`${this.baseUrl}/certificate/assign/${courseId}`, requestBody)
            .pipe(
                tap(() => {
                    // Certificate assignment successful
                }),
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    // Method to get students with optional filtering by certificate type and physical issuance
    getStudentsWithFilters(
        courseId: string,
        certificateTypes?: ('COMPLETION' | 'PARTICIPATION' | 'NO_PARTICIPATION')[] | null,
        isIssuedPhysically?: string[] | null,
        searchParam?: string | null,
        page: number = 1,
        size: number = 100,
    ): Observable<CertificateFilterResponse> {
        let params = new HttpParams();

        // Add courseId as path parameter (already in URL)

        // Add certificateTypes parameter if provided
        if (certificateTypes && certificateTypes.length > 0) {
            const certificateTypesParam = certificateTypes.join(',');
            params = params.set('certificateTypes', certificateTypesParam);
        }

        // Add isIssuedPhysically parameter if provided
        if (isIssuedPhysically && isIssuedPhysically.length > 0) {
            const isIssuedPhysicallyParam = isIssuedPhysically.join(',');
            params = params.set('isIssuedPhysically', isIssuedPhysicallyParam);
        }

        // Add searchParam if provided
        if (searchParam?.trim()) {
            params = params.set('searchParam', searchParam.trim());
        }

        // Add pagination parameters
        params = params.set('page', page.toString());
        params = params.set('size', size.toString());

        return this.http.get<CertificateFilterResponse>(`${this.baseUrl}/certificate/status/${courseId}`, { params });
    }

    // Method to update certificate type for a student
    updateCertificateType(
        courseId: string,
        payload: CertificateTypeUpdatePayload,
    ): Observable<CertificateTypeUpdateResponse> {
        return this.http.patch<CertificateTypeUpdateResponse>(
            `${this.baseUrl}/certificate/certification-type/${courseId}`,
            payload,
        );
    }

    // Method to bulk assign certificates to multiple students
    bulkAssignCertificates(
        courseId: string,
        payload: BulkCertificateAssignmentPayload[],
    ): Observable<BulkCertificateAssignmentResponse> {
        // Filter out any payload items with null/empty certificate types
        const filteredPayload = payload.filter(
            (item) => item.certificateStatus !== null && item.certificateStatus !== undefined,
        );

        if (filteredPayload.length === 0) {
            return new Observable((observer) => {
                observer.next({ message: 'No valid certificate assignments to process' });
                observer.complete();
            });
        }

        return this.http
            .patch<BulkCertificateAssignmentResponse>(`${this.baseUrl}/certificate/status/${courseId}`, filteredPayload)
            .pipe(
                tap(() => {
                    // Bulk certificate assignment successful
                }),
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    // Method to toggle physical certificate for a student
    togglePhysicalCertificate(
        courseId: string,
        payload: PhysicalCertificateTogglePayload,
    ): Observable<PhysicalCertificateToggleResponse> {
        // Convert single payload to array as API expects array format
        const payloadArray = [payload];

        return this.http
            .patch<PhysicalCertificateToggleResponse>(
                `${this.baseUrl}/certificate/course/${courseId}/physical-certificate`,
                payloadArray,
            )
            .pipe(
                tap(() => {
                    // Physical certificate toggle successful
                }),
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }
}
